import * as types from 'src/store/mutation-types'
import { Notify, date } from 'quasar'
import { StatisticsClient } from 'src/api/Statistics'
import { TimeEntriesClient } from 'src/api/TimeEntries'
import { createErrorFromResponse } from 'src/utils/error'
import i18n from 'src/i18n/i18n'

// initial state
// shape: [{ id, quantity }]
const state = {
  timeTrackingStatisticsLoaded: false,
  projectSeconds: null,
  unbilledSeconds: null,
  billableSeconds: null,
  timeEntries: [],
  timeEntriesMeta: null,
  timeEntriesLoaded: false,
  activeTimeEntry: null
}

// getters
const getters = {
  timeTrackingStatisticsLoaded: state => state.timeTrackingStatisticsLoaded,
  projectSeconds: state => state.projectSeconds,
  unbilledSeconds: state => state.unbilledSeconds,
  billableSeconds: state => state.billableSeconds,
  timeEntries: state => state.timeEntries,
  timeEntriesMeta: state => state.timeEntriesMeta,
  timeEntriesLoaded: state => state.timeEntriesLoaded,
  activeTimeEntry: state => state.activeTimeEntry
}

const paginationDefault = {
  page: 1,
  rowsNumber: 0,
  rowsPerPage: 20,
  descending: true,
  sortBy: 'started_at',
  filterBy: null,
  include: null
}

const DEFAULT_INCLUDES = [
  'customer',
  'project',
  'user'
]

const actions = {
  fetchTimeTrackingStatisticsOverview ({ commit }) {
    const now = new Date()
    const startOfMonth = date.formatDate(new Date(now.getFullYear(), now.getMonth(), 1), 'YYYY-MM-DD')

    const data = {
      'statistics': [
        { 'name': 'project_seconds', options: { 'unit': 'month', 'start_date': startOfMonth } },
        { 'name': 'unbilled_seconds' },
        { 'name': 'billable_seconds', options: { 'unit': 'month', 'start_date': startOfMonth } }
      ]
    }

    return new StatisticsClient()
      .list(data)
      .then(response => {
        const values = response.data.data

        let data = {
          projectSeconds: values.find(statistic => statistic.name === 'project_seconds'),
          unbilledSeconds: values.find(statistic => statistic.name === 'unbilled_seconds'),
          billableSeconds: values.find(statistic => statistic.name === 'billable_seconds')
        }

        commit(types.SET_TIME_TRACKING_STATISTICS, data)
        commit(types.SET_TIME_TRACKING_STATISTICS_LOADED, true)
      })
  },

  listTimeEntries ({ commit, state }, { pagination } = {}) {
    pagination = { ...paginationDefault, ...(pagination || {}) }

    const sort = [pagination.sortBy, pagination.descending ? 'desc' : 'asc']
    commit(types.SET_TIME_ENTRIES_LOADED, false)

    return new TimeEntriesClient()
      .include(pagination.include || DEFAULT_INCLUDES)
      .filter(pagination.filterBy || [])
      .sort(sort)
      .page(pagination.page)
      .limit(pagination.rowsPerPage)
      .list()
      .then(response => {
        commit(types.SET_TIME_ENTRIES, response.data.data)
        commit(types.SET_TIME_ENTRIES_META, response.data.meta)
        commit(types.SET_TIME_ENTRIES_LOADED, true)
      })
      .then(() => Promise.resolve({ timeEntries: state.timeEntries, meta: state.timeEntriesMeta }))
  },

  fetchNextTimeEntriesChunk ({ commit, state }, { done, pagination = {} }) {
    if (!state.timeEntriesLoaded) {
      return done()
    } else if (!state.timeEntriesMeta) {
      return done(true)
    }

    pagination = { ...paginationDefault, ...(pagination || {}) }
    const sort = [pagination.sortBy, pagination.descending ? 'desc' : 'asc']

    let currentPage = state.timeEntriesMeta.pagination.current_page
    let totalPages = state.timeEntriesMeta.pagination.total_pages

    // Check if there is still a chunk to fetch available.
    if (currentPage < totalPages) {
      return new TimeEntriesClient()
        .include(pagination.include || DEFAULT_INCLUDES)
        .filter(pagination.filterBy || [])
        .sort(sort)
        .limit(pagination.rowsPerPage)
        .page(currentPage + 1)
        .list()
        .then(response => {
          let newTimeEntries = state.timeEntries.concat(response.data.data)

          commit(types.SET_TIME_ENTRIES, newTimeEntries)
          commit(types.SET_TIME_ENTRIES_META, response.data.meta)

          // Indicate that data has been loaded successfully.
          done()
        })
    } else {
      // No more data available to fetch.
      // Passing true to the callback function tells the infinite scroll component to stop loading.
      done(true)
    }
  },

  searchTimeEntries ({ commit, state }, { pagination, filter }) {
    commit(types.SET_TIME_ENTRIES_LOADED, false)

    return new TimeEntriesClient()
      .search(filter)
      .then(response => {
        commit(types.SET_TIME_ENTRIES, response.data.data)
        commit(types.SET_TIME_ENTRIES_META, {
          pagination: {
            count: response.data.data.length,
            current_page: pagination.page,
            per_page: pagination.rowsPerPage,
            total: response.data.data.length,
            total_pages: 1
          }
        })
        commit(types.SET_TIME_ENTRIES_LOADED, true)
      })
      .then(() => Promise.resolve({ timeEntries: state.timeEntries, meta: state.timeEntriesMeta }))
  },

  fetchActiveTimeEntry ({ commit }) {
    return new TimeEntriesClient()
      .include(DEFAULT_INCLUDES)
      .active()
      .then(response => {
        commit(types.SET_ACTIVE_TIME_ENTRY, response.data.data)
      })
      .catch(() => {
        commit(types.SET_ACTIVE_TIME_ENTRY, null)
      })
  },

  createTimeEntry ({ commit }, data) {
    return new TimeEntriesClient()
      .include(DEFAULT_INCLUDES)
      .create(data)
      .then(response => {
        let timeEntry = response.data.data
        commit(types.SET_ACTIVE_TIME_ENTRY, timeEntry)

        return timeEntry
      })
  },

  updateTimeEntry ({ commit, state }, { timeEntryId, timeEntry }) {
    return new TimeEntriesClient()
      .include(DEFAULT_INCLUDES)
      .update(timeEntryId, timeEntry)
      .then(response => {
        let timeEntry = response.data.data

        commit(types.UPDATE_TIME_ENTRY, timeEntry)

        if (state.activeTimeEntry && state.activeTimeEntry.id === timeEntry.id && !!timeEntry.stopped_at) {
          commit(types.SET_ACTIVE_TIME_ENTRY, null)
          commit(types.ADD_TIME_ENTRY, timeEntry)
        }

        return timeEntry
      })
  },

  deleteTimeEntry ({ commit, state }, timeEntry) {
    return new TimeEntriesClient()
      .destroy(timeEntry.id)
      .then(() => {
        commit(types.REMOVE_TIME_ENTRY, timeEntry)

        if (state.timeEntriesMeta) {
          commit(types.SET_TIME_ENTRIES_META, {
            pagination: {
              ...state.timeEntriesMeta,
              total: state.timeEntriesMeta.pagination.total - 1
            }
          })
        }
      })
      .then(() => Promise.resolve({ timeEntries: state.timeEntries, meta: state.timeEntriesMeta }))
  },

  deleteTimeEntries ({ commit, state }, timeEntries) {
    let ids = timeEntries.map(timeEntry => timeEntry.id)

    return new TimeEntriesClient()
      .destroy(ids)
      .then(() => timeEntries.forEach(timeEntry => commit(types.REMOVE_TIME_ENTRY, timeEntry)))
      .then(() => commit(types.SET_TIME_ENTRIES_META, {
        pagination: {
          ...state.timeEntriesMeta.pagination,
          total: state.timeEntriesMeta.pagination.total - timeEntries.length
        }
      }))
      .then(() => Notify.create({ message: i18n.tc('time_entry_deleted', 2), type: 'positive' }))
      .then(() => Promise.resolve({ timeEntries: state.timeEntries, meta: state.timeEntriesMeta }))
      .catch(createErrorFromResponse)
  }
}

// mutations
const mutations = {
  [types.SET_TIME_TRACKING_STATISTICS] (state, { projectSeconds, billableSeconds, unbilledSeconds }) {
    state.projectSeconds = projectSeconds
    state.billableSeconds = billableSeconds
    state.unbilledSeconds = unbilledSeconds
  },

  [types.SET_TIME_TRACKING_STATISTICS_LOADED] (state, value) {
    state.timeTrackingStatisticsLoaded = value
  },

  [types.SET_TIME_ENTRIES] (state, timeEntries) {
    state.timeEntries = timeEntries
  },

  [types.SET_TIME_ENTRIES_META] (state, meta) {
    state.timeEntriesMeta = meta
  },

  [types.SET_TIME_ENTRIES_LOADED] (state, value) {
    state.timeEntriesLoaded = value
  },

  [types.SET_ACTIVE_TIME_ENTRY] (state, timeEntry) {
    state.activeTimeEntry = timeEntry
  },

  [types.ADD_TIME_ENTRY] (state, timeEntry) {
    state.timeEntries.unshift(timeEntry)
  },

  [types.UPDATE_TIME_ENTRY] (state, newTimeEntry) {
    let timeEntryIndex = state.timeEntries.findIndex(timeEntry => timeEntry.id === newTimeEntry.id)
    if (timeEntryIndex !== -1) {
      state.timeEntries.splice(timeEntryIndex, 1, newTimeEntry)
    }

    if (state.activeTimeEntry && state.activeTimeEntry.id === newTimeEntry.id) {
      state.activeTimeEntry = Object.assign({}, state.activeTimeEntry, newTimeEntry)
    }
  },

  [types.REMOVE_TIME_ENTRY] (state, timeEntry) {
    const toDelete = state.timeEntries.find(stateTimeEntry => stateTimeEntry.id === timeEntry.id)
    const index = state.timeEntries.indexOf(toDelete)

    if (index !== -1) {
      state.timeEntries.splice(index, 1)
    }

    if (state.activeTimeEntry && state.activeTimeEntry.id === timeEntry.id) {
      state.activeTimeEntry = null
    }
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
